"use client";

import dynamic from "next/dynamic";
import { useEffect, useState, type ReactNode } from "react";
import { usePathname } from "next/navigation";

import { isPerformanceCriticalPath } from "@/lib/performance-critical-paths";

const PANCHANG_CONTEXT_STRIP_HEIGHT = 34;
const CRITICAL_STRIP_DEFER_MS = 30000;
const STRIP_ACTIVATION_EVENTS = ["pointerdown", "keydown", "touchstart", "focusin"] as const;

const InteractivePanchangContextStrip = dynamic(
  () => import("@/components/PanchangContextStripInteractive"),
  {
    ssr: false,
    loading: () => <PanchangContextStripSkeleton />,
  }
);

function useDesktopPanchangStripEnabled() {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(min-width: 640px)");
    const update = () => setEnabled(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return enabled;
}

function useInteractiveStripReady(enabled: boolean) {
  const pathname = usePathname();
  const [ready, setReady] = useState(false);
  const isCriticalPath = isPerformanceCriticalPath(pathname || "/");

  useEffect(() => {
    if (!enabled || ready) return undefined;
    const activate = () => setReady(true);

    if (!isCriticalPath) {
      const timer = window.setTimeout(activate, 0);
      return () => window.clearTimeout(timer);
    }

    const timer = window.setTimeout(activate, CRITICAL_STRIP_DEFER_MS);
    const options: AddEventListenerOptions = { once: true, passive: true };
    for (const eventName of STRIP_ACTIVATION_EVENTS) {
      window.addEventListener(eventName, activate, options);
    }

    return () => {
      window.clearTimeout(timer);
      for (const eventName of STRIP_ACTIVATION_EVENTS) {
        window.removeEventListener(eventName, activate);
      }
    };
  }, [enabled, isCriticalPath, ready]);

  return ready;
}

function PanchangContextStripSkeleton() {
  return (
    <div
      aria-hidden="true"
      className="mx-auto max-w-6xl px-4 flex items-center justify-between gap-4"
      style={{ height: `${PANCHANG_CONTEXT_STRIP_HEIGHT}px` }}
    >
      <div className="flex min-w-0 items-center gap-2.5 overflow-hidden">
        <span className="h-2 w-16 shrink-0 rounded-lg bg-ish-border opacity-80" />
        <span className="h-2 w-24 shrink-0 rounded-lg bg-ish-border opacity-60" />
        <span className="hidden h-2 w-28 shrink-0 rounded-lg bg-ish-border opacity-50 md:block" />
      </div>
      <span className="h-2 w-14 shrink-0 rounded-lg bg-ish-border opacity-60" />
    </div>
  );
}

function PanchangContextStripShell({ children }: { children: ReactNode }) {
  return (
    <div
      className="static-shell-hide sticky z-30 border-b hidden sm:block"
      style={{
        top: "52px",
        background: "var(--ish-accent-light)",
        borderColor: "var(--ish-border)",
        minHeight: `${PANCHANG_CONTEXT_STRIP_HEIGHT}px`,
      }}
    >
      {children}
    </div>
  );
}

export default function PanchangContextStrip() {
  const enabled = useDesktopPanchangStripEnabled();
  const ready = useInteractiveStripReady(enabled);

  return (
    <PanchangContextStripShell>
      {enabled && ready ? <InteractivePanchangContextStrip /> : <PanchangContextStripSkeleton />}
    </PanchangContextStripShell>
  );
}
